import {MessageSquare} from "lucide-react";
import {useLocation, useNavigate} from "react-router-dom";

import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {useGetHasPermissions, useStoredMe} from "@/src/modules/core/store/me";
import {useNotificationFeed} from "@/src/modules/notification-feed/hooks/use-notification-feed";
import {getNotificationBellQueryParams} from "@/src/modules/notification-feed/lib/notification-feed-topics";

import {useDirectMessageFeedUnreadCount} from "../hooks/use-direct-message-feed-unread-count";
const DIRECT_MESSAGES_PATH = "/direct-messages";
const BADGE_MAX = 99;

type DirectMessagesSidebarLinkProps = {
  showLabel?: boolean;
  buttonClassName?: string;
};

export function DirectMessagesSidebarLink({
  showLabel = false,
  buttonClassName,
}: DirectMessagesSidebarLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const getHasPermission = useGetHasPermissions();
  const me = useStoredMe();
  const isResident = Boolean(me?.isResident);
  const bellQueryParams = getNotificationBellQueryParams(isResident);
  const hasShow = getHasPermission("directmessage show");
  useNotificationFeed({params: bellQueryParams, enabled: hasShow});
  const unreadCount = useDirectMessageFeedUnreadCount();

  const isActive =
    location.pathname === DIRECT_MESSAGES_PATH ||
    location.pathname.startsWith(`${DIRECT_MESSAGES_PATH}/`);
  const hasUnread = unreadCount > 0;
  const badgeLabel = unreadCount > BADGE_MAX ? `${BADGE_MAX}+` : unreadCount.toString();
  const showInlineBadge = showLabel && hasUnread;

  if (!hasShow) {
    return null;
  }

  return (
    <Button
      type="button"
      variant={
        showLabel ? "ghost" : hasUnread && !showLabel ? "destructiveOutline" : "outline"
      }
      size={showLabel ? "sm" : "icon"}
      className={cn(
        "relative",
        hasUnread && !showLabel && "bg-destructive/10",
        isActive && "bg-accent font-semibold text-foreground",
        buttonClassName,
      )}
      aria-label={
        hasUnread ? `Direct messages, ${unreadCount} unread` : "Direct messages"
      }
      onClick={() => navigate(DIRECT_MESSAGES_PATH)}    >
      <MessageSquare
        className={cn("size-4 shrink-0", isActive ? "text-foreground" : "text-primary")}
        aria-hidden
      />
      {showLabel ? <span>Direct messages</span> : null}
      {hasUnread && showInlineBadge ? (
        <span className="bg-destructive inline-flex min-w-6 items-center justify-center rounded-full p-1 text-xs leading-none font-bold text-white">
          {badgeLabel}
        </span>
      ) : null}
      {hasUnread && !showInlineBadge ? (
        <span className="pointer-events-none absolute bottom-1 right-1 z-10 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white">
          {badgeLabel}
        </span>
      ) : null}
    </Button>
  );
}
